import axios from 'axios';
import { isEmpty } from 'lodash';
import * as Sentry from '@sentry/react';
import AuthService from './authService';
import { store } from '../store/store';
import { selectTenantId } from '../pages/configuration/participantSignupSlice';
import { convertAllDecimalsToFourPlacesOptimized } from '../utils/global';
import { getDecimalConfig, consumeNextRequestConfig } from '../utils/apiDecimalConfig';

const requestArray: any = [];
const authService = new AuthService();

export const axiosInstance = axios.create({
  url: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '',
});
export const cancelAxios = axios.CancelToken;

let busyApiCall = false;
let isRetrying = false;
let refreshPromise: Promise<string | null> | null = null;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

const params = new URLSearchParams(window.location.search);
const type = params.get('type');

// Process the failed queue with the new token or null if refresh failed
const processQueue = (token: string | null, error: any = null) => {
  console.log(
    `[ProcessQueue] Processing ${failedQueue.length} queued requests: ${
      token ? 'with new token' : 'failed - no token'
    }`
  );

  failedQueue.forEach((request, index) => {
    if (token) {
      // Retry the request with the new token
      const retryConfig = { ...request.config };

      // Ensure headers exist
      if (!retryConfig.headers) {
        retryConfig.headers = {};
      }

      // Update the Authorization header with the new token
      retryConfig.headers.Authorization = `Bearer ${token}`;

      // Ensure method is preserved (important for POST/PUT requests)
      if (!retryConfig.method && request.config.method) {
        retryConfig.method = request.config.method;
      }

      // Ensure data is preserved for POST/PUT requests
      if (
        (retryConfig.method === 'post' ||
          retryConfig.method === 'put' ||
          retryConfig.method === 'patch') &&
        !retryConfig.data &&
        request.config.data
      ) {
        retryConfig.data = request.config.data;
      }

      // Preserve content-type header for requests with data
      if (retryConfig.data && request.config.headers && request.config.headers['Content-Type']) {
        retryConfig.headers['Content-Type'] = request.config.headers['Content-Type'];
      }

      console.log(
        `[ProcessQueue] Retrying request ${index + 1}/${failedQueue.length} to ${
          retryConfig.url
        } (${retryConfig.method || 'GET'})`
      );

      axiosInstance(retryConfig)
        .then((response) => {
          console.log(
            `[ProcessQueue] Request ${index + 1}/${failedQueue.length} succeeded after retry`
          );

          // Convert decimal values in retry response as well
          if (response.data) {
            try {
              // Get config from the original request
              const retryDecimalConfig = getDecimalConfig(retryConfig);
              const retrySkipAll = retryDecimalConfig.skipDecimalConversion;
              const retryExcludeFields = retryDecimalConfig.excludeDecimalFields || [];

              if (!retrySkipAll) {
                response.data = convertAllDecimalsToFourPlacesOptimized(response.data, {
                  maxDepth: 50,
                  inPlace: false,
                  shallow: false,
                  excludeFields: retryExcludeFields,
                });
              }
            } catch (conversionError) {
              console.error('[ProcessQueue] Failed to convert decimal values:', conversionError);
            }
          }

          request.resolve(response);
        })
        .catch((err) => {
          console.error(
            `[ProcessQueue] Request ${index + 1}/${failedQueue.length} failed after retry:`,
            err.message
          );
          request.reject(err);
        });
    } else {
      // If token refresh failed, reject all requests in queue
      console.log(
        `[ProcessQueue] Rejecting request ${index + 1}/${
          failedQueue.length
        } due to failed token refresh`
      );
      request.reject(error);
    }
  });

  // Reset the queue and refreshing state
  console.log('[ProcessQueue] Reset queue and state');
  failedQueue = [];
  isRetrying = false;
  refreshPromise = null;
};

// Add a request interceptor
axiosInstance.interceptors.request.use(
  async function (config) {
    config.headers = {
      ...config.headers,
    };

    // Get tenant ID from Redux store and add header only if not already present
    if (!config.headers['X-Tenant-Id']) {
      const state = store.getState();
      const tenantId = selectTenantId(state);

      // Add X-Tenant-Id header if tenantId exists
      if (tenantId) {
        config.headers['X-Tenant-Id'] = tenantId;
      }
    }

    // Attach decimal conversion config to axios config
    const decimalConfig = consumeNextRequestConfig();
    if (decimalConfig) {
      (config as any).__decimalConfig = decimalConfig;
    }

    // Check if token is expired and refresh it before making the request
    if (authService.isTokenExpired() && !type) {
      console.log('[Request Interceptor] Token is expired, attempting to refresh');
      try {
        const newToken = await authService.signInSilent();
        if (newToken) {
          console.log('[Request Interceptor] Token refreshed successfully');
          // Add the new token to the request
          config.headers.Authorization = `Bearer ${newToken}`;
        } else {
          console.log('[Request Interceptor] Token refresh returned null');
        }
      } catch (error) {
        console.error('[Request Interceptor] Failed to refresh token:', error);
        // Continue with the request - the response interceptor will handle 401 errors
      }
    } else {
      // If token is valid, add it to the request
      const token = authService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  async function (error: any) {
    return await Promise.reject(error);
  }
);

// Helper to detect network/CORS errors (API down scenarios)
const isNetworkError = (error: any): boolean => {
  // Network errors occur when:
  // 1. No response is received (API down, CORS blocked, network failure)
  // 2. Error code indicates network issue
  const networkErrorCodes = [
    'ERR_NETWORK',
    'ECONNABORTED',
    'ETIMEDOUT',
    'ENOTFOUND',
    'ECONNREFUSED',
  ];
  return (
    !error.response &&
    error.request &&
    (error.message === 'Network Error' ||
      error.code === 'ERR_NETWORK' ||
      networkErrorCodes.includes(error.code))
  );
};

// Helper to get error type for categorization
const getErrorType = (error: any): string => {
  if (axios.isCancel(error)) return 'CANCELLED';
  if (isNetworkError(error)) return 'NETWORK_ERROR';
  if (error.code === 'ECONNABORTED') return 'TIMEOUT';
  if (error.response) return `HTTP_${error.response.status}`;
  return 'UNKNOWN';
};

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    if (requestArray.length !== 0) {
      requestArray.forEach(function (x: any, i: any) {
        if (response.config.url === x.url) {
          requestArray.splice(i, 1);
        }
      });
    }
    // Get decimal conversion config from the request
    const decimalConfig = getDecimalConfig(response.config);
    const shouldSkipAll = decimalConfig.skipDecimalConversion;
    const excludeFields = decimalConfig.excludeDecimalFields || [];

    // Convert decimal values (unless entire response is skipped)
    if (response.data && !shouldSkipAll) {
      try {
        response.data = convertAllDecimalsToFourPlacesOptimized(response.data, {
          maxDepth: 50, // Limit depth to prevent stack overflow
          inPlace: false, // Create new object to avoid mutation
          shallow: false, // Deep conversion for nested structures
          excludeFields: excludeFields, // Fields to skip conversion (keep raw values)
        });
      } catch (error) {
        // If conversion fails, log error but return original response
        console.error('[API Interceptor] Failed to convert decimal values:', error);
      }
    }

    return response;
  },
  async function (error: any) {
    const correlationId = error.response?.headers?.['x-error-correlation-id'];
    const errorType = getErrorType(error);
    const isNetworkOrCorsError = isNetworkError(error);

    // Build extra details with enhanced network error information
    const extra_details: Record<string, any> = {
      errorType,
      errorMessage: error.message,
      errorCode: error.code,
      correlationId: correlationId,
      // Request details (always available)
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      requestBaseURL: error.config?.baseURL,
      requestData: error.config?.data,
      requestHeaders: error.config?.headers,
    };

    // Add response details if available (not available for network/CORS errors)
    if (error.response) {
      extra_details.responseStatus = error.response.status;
      extra_details.responseData = JSON.stringify(error.response.data, null, 2);
      extra_details.responseHeaders = error.response.headers;
    }

    // Add network error specific details
    if (isNetworkOrCorsError) {
      extra_details.networkErrorDetails = {
        message: error.message,
        code: error.code,
        // Note: This could be CORS error when API is down
        possibleCause:
          'API may be down, network failure, or CORS blocked due to no response headers',
        hasRequest: !!error.request,
        hasResponse: !!error.response,
      };
    }

    // Capture to Sentry with tags for better filtering/grouping
    Sentry.captureException(error, {
      tags: {
        errorType,
        isNetworkError: isNetworkOrCorsError,
        httpStatus: error.response?.status?.toString() || 'N/A',
        endpoint: error.config?.url || 'unknown',
      },
      extra: extra_details,
      // Custom fingerprint for better error grouping
      fingerprint: isNetworkOrCorsError
        ? ['network-error', error.config?.url || 'unknown-url']
        : ['{{ default }}'],
    });

    // Store the original request
    const originalRequest = error.config;
    requestArray.push(originalRequest);

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.log('[Response Interceptor] 401 Unauthorized error, queueing request for retry');
      const retryRequest = new Promise((resolve, reject) => {
        // Add to queue to retry after token refresh
        failedQueue.push({
          resolve,
          reject,
          config: originalRequest,
        });
      });

      // If we're not already refreshing the token, initiate refresh
      if (!isRetrying) {
        console.log('[Response Interceptor] Starting token refresh');
        isRetrying = true;

        // Create a promise for the token refresh that can be reused
        refreshPromise = new Promise<string | null>((resolve) => {
          authService
            .signInSilent()
            .then((token) => {
              // We got a new token, resolve with it
              console.log('[Response Interceptor] Token refresh successful');
              resolve(token as string);
            })
            .catch((refreshError) => {
              // Failed to get token
              console.error('[Response Interceptor] Failed to refresh token:', refreshError);
              resolve(null);
            });
        });

        // Process the token result
        refreshPromise.then((accessToken) => {
          if (accessToken) {
            console.log('[Response Interceptor] Processing retry queue with new token');
            // Process the queue with the new token
            processQueue(accessToken);
          } else {
            console.log('[Response Interceptor] No token obtained, rejecting all queued requests');
            // If we couldn't get a new token, reject all requests
            processQueue(null, error);

            // If no active API call is in progress, dispatch SIGN_IN event
            if (!busyApiCall) {
              busyApiCall = true;
              console.log('[Response Interceptor] Dispatching SIGN_IN event');
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('PERMISSION_CHANGED', { detail: 'SIGN_IN' }));
              }, 1000);
            }
          }
        });
      } else {
        console.log('[Response Interceptor] Token refresh already in progress, request queued');
      }

      // Return the promise that will resolve/reject based on the token refresh
      return retryRequest;
    }

    if (error.response?.status === 403 && isEmpty(error?.response?.data?.error)) {
      if (!busyApiCall) {
        busyApiCall = true;
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('PERMISSION_CHANGED', { detail: 'REFRESH' }));
        }, 1000);
      }
    }

    return await Promise.reject(error);
  }
);
